import React from 'react';
import dayjs from 'dayjs';
import { EditNotifications } from '@mui/icons-material';
import { evaluationPermission } from './evaluationPermission';
import type {
  ScholarshipNomination,
  SubScoresRecord,
  DimensionData,
  SubScoresRecords,
  ScholarshipQuestion,
  ScholarshipNominationMetadata,
} from './types';
import { type EVALUATION_SCENE } from './constant';
import { FundingSource } from '../FundingModule';
import { EVALUATION_TEXT } from './lang';
import { EvaluationForm } from './EvaluationForm';
import {
  Alert,
  Loading,
  RemarksNotificationText,
} from '../../../../../components';
import {
  useGetEvaluationFormInfo,
  useGetApplicationPanelRemarks,
} from '../../../graphql/NominationForm';
import { type ROLES_MODULE } from '../../../../../components/RoleSwitchBar/roles';
import { useAuth } from '../../../../../contexts';
import {
  getSimplifyRole,
  SimplifyRoles,
} from '../../../../../utils/enableEditApplication/enableEditApplication';
import { PolishStatus } from '../../../../../utils/getApplicationViewsPaginated/types';

type EvaluationProps = {
  scene: EVALUATION_SCENE;
  applicationId: string;
  scholarshipNomination: ScholarshipNomination;
  refetchEvaluation: () => void;
};

const headText: Partial<Record<SimplifyRoles, string>> = {
  [SimplifyRoles.SUPERVISOR]:
    EVALUATION_TEXT.HEAD_HELPER_TEXT.SUPERVISOR_HELPER_TEXT,
  [SimplifyRoles.INTERVIEW_PANEL]:
    EVALUATION_TEXT.HEAD_HELPER_TEXT.INTERVIEW_PANEL_HELPER_TEXT,
  [SimplifyRoles.DORPGS]: EVALUATION_TEXT.HEAD_HELPER_TEXT.DORPG_HELPER_TEXT,
};

const NA = 'N/A';
export const handleCheckFinishEvaluationQuestions = (
  scholarshipNominationMetadata: ScholarshipNominationMetadata,
  questions: ScholarshipQuestion[],
  role:
    | SimplifyRoles.SUPERVISOR
    | SimplifyRoles.INTERVIEW_PANEL
    | SimplifyRoles.DORPGS,
): boolean => {
  // *reduce* to get the question number that need to completed by the role based on matadata validate role
  // *every* to check the whether specific questions are all completed
  const allQuestionCompleted = Object.values(scholarshipNominationMetadata)
    ?.reduce((acc: string[], categoriedQuestions: any) => {
      const roleQuestionsNumbers: string[] = categoriedQuestions
        ?.filter((question: any) => {
          return question?.validUserRoles?.includes(role);
        })
        ?.map((question: any) => {
          return question?.mappedNumber;
        });
      return [...acc, ...roleQuestionsNumbers];
    }, [])
    ?.every(questionNumber => {
      const question = questions.find(
        (q: any) => q?.questionNumber === questionNumber,
      );
      const comment = question?.comment ?? '';
      return Boolean(comment.trim());
    });

  return allQuestionCompleted;
};

export const Evaluation: React.FC<EvaluationProps> = props => {
  const { role } = useAuth();
  const currentRole = getSimplifyRole(role as ROLES_MODULE);

  const { scene, applicationId, scholarshipNomination, refetchEvaluation } =
    props;
  const {
    scores,
    endorsement,
    UPAFDimension = [],
    justification,
    interviewPanel,
    HKPFSDimension = [],
    sectionalScores = [],
    eligibilityCheck = [],
    candidatesDimension = [],
    academicProgramme,
    matchedQuestionsData,
    polishStatus,
  } = scholarshipNomination;
  const {
    academicExcellenceQuestions,
    researchPotentialQuestions,
    strengthsQuestions,
    distinctiveQualityQuestions,
  } = matchedQuestionsData;

  const processDimensionHistory = (
    dimensions: DimensionData[],
    isCandidates = false,
  ): SubScoresRecords[] => {
    const groupedByDate = dimensions
      .flatMap((dimension: DimensionData) => {
        const { history } = dimension;
        return history
          .filter(record => record.candidate === isCandidates)
          .map(record => ({
            ...record,
            dimensionName: dimension.dimensionName,
          }));
      })
      .reduce(
        (
          acc: Record<string, Record<string, SubScoresRecord[]>>,
          record: SubScoresRecord,
        ) => {
          const date = dayjs(record.createdAt).format('YYYY-MM-DD');
          const time = dayjs(record.createdAt).format('HH:mm:ss');
          acc[date] = {
            ...acc[date],
            [time]: [...(acc[date]?.[time] ?? []), record],
          };
          return acc;
        },
        {},
      );

    return Object.entries(groupedByDate)
      .sort(([dateA], [dateB]) => dayjs(dateB).diff(dayjs(dateA))) // Sort dates in ascending order
      .map(([date, times]) => ({
        date,
        content: Object.entries(times)
          .sort(([timeA], [timeB]) =>
            dayjs(timeB, 'HH:mm:ss').diff(dayjs(timeA, 'HH:mm:ss')),
          ) // Sort times in ascending order
          .map(([time, dimensions]) => ({
            time,
            dimensions,
          })),
      }));
  };

  // Distract history data from dimension
  const UPAFHistory = processDimensionHistory(UPAFDimension ?? []);
  const HKPFSHistory = processDimensionHistory(HKPFSDimension ?? []);
  const candidatesHistory = processDimensionHistory(
    candidatesDimension ?? [],
    true,
  );

  const {
    evaluationInfo: {
      researchAssessment,
      firstDegreeAssessment,
      fundingSourceConfiguration,
    },
    loading,
    error,
    refetch: refetchResearchAssessment,
  } = useGetEvaluationFormInfo(applicationId);

  const currentHKPFSPanel = process.env.REACT_APP_CURRENT_HKPFS_PANEL ?? '';

  const allowPolishComments = polishStatus === PolishStatus.IN_POLISH_FLOW;

  const {
    remark,
    loading: remarkLoading,
    error: remarkError,
  } = useGetApplicationPanelRemarks(
    currentHKPFSPanel,
    !currentHKPFSPanel || !applicationId || !allowPolishComments,
    {
      applicationId: { eq: applicationId },
    },
  );

  const displayRemark = remark.length > 0 ? remark : NA;

  if (error ?? remarkError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: 'Error Occurs.' + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  // process evaluation visual and edit permission
  // Just use the data from the scholarshipMetadata and scholarshipNomination eg. validUserRoles and editableRoles tags
  const UPAFWithoutHKPFSArr = [
    ...(fundingSourceConfiguration?.mapping?.COMPETITIVE?.filter(
      funding => funding.value !== FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME,
    ) ?? []),
    ...(fundingSourceConfiguration?.mapping?.NON_COMPETITIVE ?? []),
  ].map(funding => funding.value);

  const hasHKPFS = scores.some(
    score => score.funding === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME,
  );

  const hasUPAFWithoutHKPFS = scores.some(score =>
    UPAFWithoutHKPFSArr.includes(score.funding),
  );

  const evaluationVisiblePermission = evaluationPermission(
    currentRole,
    scene,
    hasHKPFS,
    hasUPAFWithoutHKPFS,
    academicExcellenceQuestions,
    researchPotentialQuestions,
    strengthsQuestions,
    distinctiveQualityQuestions,
    HKPFSDimension,
    UPAFDimension,
  );

  const notification = headText[currentRole] ?? '';

  const evaluationFormProps = {
    applicationId,
    academicProgramme,
    scores,
    UPAFDimension,
    interviewPanel,
    HKPFSDimension,
    UPAFHistory,
    HKPFSHistory,
    candidatesDimension,
    candidatesHistory,
    sectionalScores,
    justification,
    endorsement,
    academicExcellenceQuestions,
    researchPotentialQuestions,
    strengthsQuestions,
    distinctiveQualityQuestions,
    firstDegreeAssessment,
    researchAssessment,
    evaluationVisiblePermission,
    eligibilityCheck,
    refetchResearchAssessment,
    refetchEvaluation,
  };

  return loading || remarkLoading ? (
    <Loading />
  ) : (
    <div className="w-full">
      {evaluationVisiblePermission.displayNotification && (
        <div className="w-full p-4">
          <EditNotifications
            style={{
              color: '#951d1d',
            }}
          />
          <span
            style={{
              margin: '0 0 0 0.5rem',
            }}
            dangerouslySetInnerHTML={{
              __html: notification,
            }}
          />
        </div>
      )}
      {evaluationVisiblePermission.panelMeetingRemarksPermit && (
        <div className="w-full my-4">
          <RemarksNotificationText
            remarksTitle={EVALUATION_TEXT.PANEL_MEETING_REMARKS}
            note={displayRemark}
          />
        </div>
      )}
      <EvaluationForm {...evaluationFormProps} />
    </div>
  );
};
